/**
 * Re-export the file-based review comments hook as the default implementation.
 * Comments are stored in .ona/review-comments-<envId>.json (relative to workspace folder)
 */
export { useFileBasedReviewComments as useReviewComments } from "@/components/ai/review/use-file-based-review-comments";

import type { ReviewComment } from "@/components/ai/review/types";
import type { useFileBasedReviewComments } from "@/components/ai/review/use-file-based-review-comments";

/**
 * Subset of the useReviewComments return value needed by components that
 * display and mutate review comments (CodeChangesPanel, FileDiffPanel, etc.).
 * Derived from the hook's return type so it stays in sync automatically.
 */
export type ReviewCommentsHandle = Pick<
    ReturnType<typeof useFileBasedReviewComments>,
    | "comments"
    | "commentsByFile"
    | "addComment"
    | "updateComment"
    | "removeComment"
    | "clearAllComments"
    | "setCommentThumbsUp"
    | "setCommentThumbsDown"
>;

/**
 * Formats review comments into a message suitable for sending to the agent.
 */
export const formatReviewCommentsForAgent = (comments: ReviewComment[], additionalContext?: string): string => {
    if (comments.length === 0) {
        return "";
    }

    const lines = ["Please address the following review feedback:\n"];

    for (const comment of comments.sort((a, b) => a.filePath.localeCompare(b.filePath) || a.startLine - b.startLine)) {
        const lineRange =
            comment.startLine === comment.endLine
                ? `line ${comment.startLine}`
                : `lines ${comment.startLine} to ${comment.endLine}`;
        lines.push(`### ${comment.filePath} (${lineRange})`);
        lines.push(comment.content);
        lines.push("\n");
    }

    if (additionalContext) {
        lines.push("---\n");
        lines.push("Additional context:");
        lines.push(additionalContext);
    }

    return lines.join("\n");
};
